/**
 * Frontend RapidFuzz-style intent matcher over the Phase A catalog.
 * Used for snappy form/project-pick open; backend agentMessage remains source of truth for execution.
 */
import {
  getMatchableAgentIntents,
  normalizeAgentIntentInput,
  type AgentIntentCandidate,
  type AgentIntentDefinition,
  type AgentIntentResolution,
} from './agentIntentCatalog';

/** Keep aligned with agent.intent.fuzzy.* / agent.intent.llm.* in application.properties */
export const AGENT_INTENT_FUZZY_ACCEPT = 88;
export const AGENT_INTENT_FUZZY_AMBIGUOUS_MIN = 70;
export const AGENT_INTENT_FUZZY_AMBIGUITY_GAP = 6;

export type AgentIntentFuzzyContext = {
  hasSelectedRequest?: boolean;
  hasSelectedProject?: boolean;
  lastOfferedIntentId?: string;
};

type ScoredIntent = {
  intent: AgentIntentDefinition;
  score: number;
  matchedPhrase: string;
};

function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  const rows = a.length + 1;
  const cols = b.length + 1;
  const prev = new Array<number>(cols);
  const cur = new Array<number>(cols);
  for (let j = 0; j < cols; j += 1) prev[j] = j;
  for (let i = 1; i < rows; i += 1) {
    cur[0] = i;
    for (let j = 1; j < cols; j += 1) {
      const cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
      cur[j] = Math.min(cur[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
    }
    for (let j = 0; j < cols; j += 1) prev[j] = cur[j];
  }
  return prev[cols - 1];
}

/** 0–100 similarity from edit distance (closest to FuzzyWuzzy ratio). */
function ratio(a: string, b: string): number {
  if (!a && !b) return 100;
  if (!a || !b) return 0;
  const dist = levenshtein(a, b);
  const maxLen = Math.max(a.length, b.length);
  return Math.round(((maxLen - dist) / maxLen) * 100);
}

function partialRatio(a: string, b: string): number {
  if (!a || !b) return 0;
  const shorter = a.length <= b.length ? a : b;
  const longer = a.length <= b.length ? b : a;
  if (longer.includes(shorter)) return 100;
  let best = 0;
  const window = shorter.length;
  for (let i = 0; i <= longer.length - window; i += 1) {
    best = Math.max(best, ratio(shorter, longer.slice(i, i + window)));
    if (best === 100) return 100;
  }
  return best;
}

function tokenSortRatio(a: string, b: string): number {
  const sortTokens = (s: string) =>
    s
      .split(/\s+/)
      .filter(Boolean)
      .sort()
      .join(' ');
  return ratio(sortTokens(a), sortTokens(b));
}

function tokenSetRatio(a: string, b: string): number {
  const ta = new Set(a.split(/\s+/).filter(Boolean));
  const tb = new Set(b.split(/\s+/).filter(Boolean));
  if (!ta.size || !tb.size) return 0;
  const intersection = Array.from(ta)
    .filter((t) => tb.has(t))
    .sort();
  const diffA = Array.from(ta)
    .filter((t) => !tb.has(t))
    .sort();
  const diffB = Array.from(tb)
    .filter((t) => !ta.has(t))
    .sort();
  const combined = intersection.join(' ');
  const aJoined = intersection.concat(diffA).join(' ');
  const bJoined = intersection.concat(diffB).join(' ');
  return Math.max(ratio(combined, aJoined), ratio(combined, bJoined), ratio(aJoined, bJoined));
}

/** Approximate FuzzyWuzzy WRatio used on the backend (without over-using token-set). */
export function weightedRatio(a: string, b: string): number {
  const r = ratio(a, b);
  const pr = partialRatio(a, b);
  const tsr = tokenSortRatio(a, b);
  return Math.max(r, Math.round(pr * 0.95), tsr);
}

function scoreIntent(normalized: string, intent: AgentIntentDefinition): ScoredIntent | null {
  let best = 0;
  let bestPhrase: string | null = null;
  for (const phrase of intent.phrases) {
    if (!phrase?.trim()) continue;
    const np = normalizeAgentIntentInput(phrase);
    if (!np) continue;
    let score = weightedRatio(normalized, np);
    // Exact phrase match: keep ahead of long-phrase partials (FE WRatio ≠ Java FuzzyWuzzy).
    if (normalized === np) {
      score = Math.max(score, 100) + 8;
    } else {
      score += Math.min(2, np.length / 40);
    }
    if (score > best) {
      best = score;
      bestPhrase = phrase;
    }
  }
  if (!bestPhrase) return null;

  for (const neg of intent.negativePhrases ?? []) {
    const nn = normalizeAgentIntentInput(neg);
    if (!nn) continue;
    // Prefer exact / substring only — fuzzy-near negatives over-dampen shared tokens.
    const hits = normalized === nn || normalized.includes(nn);
    if (hits) {
      best = Math.min(best, AGENT_INTENT_FUZZY_AMBIGUOUS_MIN - 1);
    }
  }
  return { intent, score: best, matchedPhrase: bestPhrase };
}

function isContextBlocked(top: ScoredIntent, context?: AgentIntentFuzzyContext): boolean {
  const required = top.intent.contextRequiredPhrases ?? [];
  if (!required.length || !top.matchedPhrase) return false;
  const matchedNorm = normalizeAgentIntentInput(top.matchedPhrase);
  const isContextPhrase = required.some((p) => normalizeAgentIntentInput(p) === matchedNorm);
  if (!isContextPhrase) return false;
  if (top.intent.id === 'execute_request') {
    if (context?.hasSelectedRequest) return false;
    return context?.lastOfferedIntentId !== 'execute_request';
  }
  return true;
}

function resolveMode(normalized: string, intent: AgentIntentDefinition): 'new' | 'existing' | undefined {
  if (intent.id !== 'create_select' || !intent.modeHints) return undefined;
  // Ambiguous create+select phrases stay mode-less (open combined picker).
  if (normalized.includes('create') && normalized.includes('select')) {
    return undefined;
  }
  const bestHint = (hints?: string[]) => {
    if (!hints?.length) return 0;
    return Math.max(0, ...hints.map((h) => weightedRatio(normalized, normalizeAgentIntentInput(h))));
  };
  const bestNew = bestHint(intent.modeHints.new);
  const bestExisting = bestHint(intent.modeHints.existing);
  if (bestNew <= 0 && bestExisting <= 0) return undefined;
  if (bestNew >= bestExisting && bestNew >= 80) return 'new';
  if (bestExisting > bestNew && bestExisting >= 80) return 'existing';
  return undefined;
}

function toResolution(
  top: ScoredIntent,
  normalized: string,
  candidates: AgentIntentCandidate[],
  needsConfirmation: boolean,
  contextRequired: boolean,
): AgentIntentResolution {
  return {
    intentId: top.intent.id,
    label: top.intent.label,
    outcome: top.intent.outcome,
    formVariant: top.intent.formVariant,
    backendAction: top.intent.backendAction,
    mode: resolveMode(normalized, top.intent),
    score: top.score,
    source: 'fuzzy',
    needsConfirmation,
    contextRequired,
    normalizedInput: normalized,
    candidates,
  };
}

/**
 * Resolve free text against the shared catalog. Returns null when fuzzy is too weak
 * (caller should fall through to legacy substring rules or backend chat).
 */
export function resolveAgentIntentFuzzy(
  rawMessage: string,
  context?: AgentIntentFuzzyContext,
): AgentIntentResolution | null {
  const normalized = normalizeAgentIntentInput(rawMessage);
  if (!normalized || normalized.length > 240) return null;

  const scored: ScoredIntent[] = [];
  for (const intent of getMatchableAgentIntents()) {
    const s = scoreIntent(normalized, intent);
    if (s && s.score > 0) scored.push(s);
  }
  scored.sort((a, b) => b.score - a.score);
  if (!scored.length) return null;

  const top = scored[0];
  const second = scored[1];
  const gap = top.score - (second?.score ?? 0);
  const candidates: AgentIntentCandidate[] = scored.slice(0, 3).map((s) => ({
    intentId: s.intent.id,
    label: s.intent.label,
    score: s.score,
    matchedPhrase: s.matchedPhrase,
  }));

  const contextBlocked = isContextBlocked(top, context);
  let accept = false;
  let ambiguous = false;

  if (contextBlocked) {
    ambiguous = top.score >= AGENT_INTENT_FUZZY_AMBIGUOUS_MIN;
  } else if (top.score >= AGENT_INTENT_FUZZY_ACCEPT) {
    if (scored.length === 1 || gap >= AGENT_INTENT_FUZZY_AMBIGUITY_GAP) {
      accept = true;
    } else {
      ambiguous = true;
    }
  } else if (top.score >= AGENT_INTENT_FUZZY_AMBIGUOUS_MIN) {
    ambiguous = true;
  }

  if (accept) {
    return toResolution(top, normalized, candidates, false, false);
  }
  if (ambiguous) {
    return toResolution(top, normalized, candidates, true, contextBlocked);
  }
  return null;
}

/** True when FE can open form/project-pick immediately without waiting on the backend. */
export function isConfidentFuzzyIntent(resolution: AgentIntentResolution | null | undefined): boolean {
  return Boolean(
    resolution &&
      (resolution.source === 'fuzzy' || resolution.source === 'llm') &&
      !resolution.needsConfirmation &&
      resolution.intentId &&
      resolution.intentId !== 'unknown' &&
      (resolution.source === 'llm'
        ? (resolution.score ?? 0) >= 60
        : (resolution.score ?? 0) >= AGENT_INTENT_FUZZY_ACCEPT),
  );
}
